import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys } from "@/lib/queries";
import type { Body } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Spinner } from "./ui";

const BodyEditor = lazy(() => import("./editor/BodyEditor"));

export function Breadcrumbs({ items, right }: { items: { label: ReactNode; to?: string }[]; right?: ReactNode }) {
  return (
    <div className="sticky top-0 z-10 flex h-11 items-center gap-2 bg-canvas/95 ps-12 pe-3 backdrop-blur md:ps-4">
      <nav className="flex min-w-0 flex-1 items-center gap-1 text-sm">
        {items.map((it, i) => (
          <span key={i} className="flex min-w-0 items-center gap-1">
            {i > 0 && <ChevronRightIcon className="size-3.5 shrink-0 text-faint rtl:rotate-180" />}
            {it.to ? (
              <Link to={it.to} className="truncate rounded px-1 py-0.5 text-muted hover:bg-hover hover:text-fg" dir="auto">
                {it.label}
              </Link>
            ) : (
              <span className="truncate px-1 text-fg" dir="auto">
                {it.label}
              </span>
            )}
          </span>
        ))}
      </nav>
      {right && <div className="flex shrink-0 items-center gap-1">{right}</div>}
    </div>
  );
}

/** Large inline-editable title that saves on blur or Enter. */
export function EditableTitle({
  value,
  onSave,
  editable,
  placeholder = "Untitled",
  className,
}: {
  value: string;
  onSave: (v: string) => Promise<unknown> | void;
  editable: boolean;
  placeholder?: string;
  className?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editable && /^Untitled/.test(value)) {
      ref.current?.focus();
      ref.current?.select();
    }
    // only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  const commit = () => {
    const v = draft.replace(/\s+/g, " ").trim();
    if (!v) {
      setDraft(value);
      return;
    }
    if (v !== value) onSave(v);
  };

  return (
    <textarea
      ref={ref}
      rows={1}
      value={draft}
      readOnly={!editable}
      placeholder={placeholder}
      dir="auto"
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
        if (e.key === "Escape") {
          setDraft(value);
          requestAnimationFrame(() => ref.current?.blur());
        }
      }}
      className={cn(
        "block w-full resize-none overflow-hidden bg-transparent text-[32px] leading-tight font-bold outline-none placeholder:text-faint",
        className,
      )}
    />
  );
}

export function SaveStatus({ status }: { status: string }) {
  if (status === "idle") return null;
  const label = { pending: "Editing…", saving: "Saving…", saved: "Saved", error: "Not saved" }[status];
  return <span className={cn("text-xs", status === "error" ? "text-danger" : "text-faint")}>{label}</span>;
}

/** Rich text body for an epic, sprint or task, autosaved; pasted images are stored in the project's files. */
export function EntityBody({
  entityId,
  title,
  initial,
  save,
}: {
  entityId: string;
  title: string;
  initial: Body;
  save: (doc: unknown[]) => Promise<unknown>;
}) {
  const { pid, canEdit } = useProject();
  const qc = useQueryClient();
  const [status, setStatus] = useState<"idle" | "pending" | "saving" | "saved" | "error">("idle");

  return (
    <div className="relative">
      <div className="absolute -top-6 end-0">
        <SaveStatus status={status} />
      </div>
      <Suspense
        fallback={
          <div className="flex h-24 items-center text-muted">
            <Spinner className="size-4" />
          </div>
        }
      >
        <BodyEditor
          key={entityId}
          initial={initial}
          editable={canEdit}
          onSave={save}
          onStatus={setStatus}
          uploadFile={async (file) => {
            const f = await api.uploadFile(pid, file, `Embedded in "${title}"`, [entityId]);
            qc.invalidateQueries({ queryKey: keys.files(pid) });
            return api.fileUrl(pid, f.id);
          }}
        />
      </Suspense>
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { id: T; label: ReactNode; icon?: React.ComponentType<{ className?: string }> }[] }) {
  return (
    <div className="flex items-center gap-1 border-b border-line">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={cn(
            "-mb-px flex items-center gap-1.5 border-b-2 px-2 pt-1 pb-2 text-sm",
            value === t.id ? "border-fg font-medium text-fg" : "border-transparent text-muted hover:text-fg",
          )}
        >
          {t.icon && <t.icon className="size-4" />}
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function SectionHeading({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold text-muted">{children}</h3>
      {right}
    </div>
  );
}
